import { Divider, Stack } from '@locus-taxy/encore-mui';
import { ProfileViewField } from './ProfileViewField';
import type { ViewField } from './profileTypes';

interface ProfileViewRendererProps {
  readonly fields: ViewField[];
  readonly columns?: number;
}

/**
 * ProfileViewRenderer — schema-driven read-only field renderer for view mode.
 * Takes an array of fields and renders them in a grid with dividers between rows.
 * Similar to EncoreFormRenderer but for display-only label/value pairs.
 */
export function ProfileViewRenderer({
  fields,
  columns = 2,
}: ProfileViewRendererProps) {
  const safeColumns = Math.max(columns, 1);
  const rows: ViewField[][] = [];
  for (let i = 0; i < fields.length; i += safeColumns) {
    rows.push(fields.slice(i, i + safeColumns));
  }

  return (
    <Stack spacing={3}>
      {rows.map((row, rowIdx) => {
        const rowKey = row.map((f) => f.label).join('-') ?? `row-${String(rowIdx)}`;
        return (
          <Stack key={rowKey} spacing={3}>
            <Stack direction="row" spacing={3}>
              {row.map((field) => (
                <ProfileViewField
                  key={field.label ?? field.value ?? 'empty'}
                  label={field.label}
                  value={field.value}
                />
              ))}
              {row.length < safeColumns &&
                Array.from({ length: safeColumns - row.length }).map((_, i) => (
                  <ProfileViewField
                    key={`${rowKey}-filler-${String(i)}`}
                    label=""
                    value={null}
                  />
                ))}
            </Stack>
            {rowIdx < rows.length - 1 && <Divider />}
          </Stack>
        );
      })}
    </Stack>
  );
}
